import { useTask } from "vig-ui-library";
import { RequestFormContext } from "../RequestFormContext";
import { SectionOrFieldGroupDto } from "../Schemas/SectionOrFieldGroupDto"
import { getRequestWithFiles, shouldHideCategory, shouldHideFieldOrSection } from "../Utilities/mappers";
import { Box, FormControlLabel, Radio, RadioGroup, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { SectionOrFieldDto } from "../Schemas/SectionOrFieldDto";
import { DeleteUploadedFileInputSchema } from "../Schemas/DeleteUploadedFileInput";

export type RadioButtonsFieldProps = RequestFormContext & {
    sectionOrFieldGroup: SectionOrFieldGroupDto;
}

export const RadioButtonsField = (props: RadioButtonsFieldProps) => {
    const sectionChild = props.sectionOrFieldGroup;
    const theme = useTheme();

    const { request, settings, onlySaveRequest, requestApi } = props;

    const saveRadioButtonTask = useTask(async () => {
        const requestWithFiles = getRequestWithFiles(request);

        for (let i = requestWithFiles.current.files.length - 1; i >= 0; i--) {
            var fileToDelete = requestWithFiles.current.files[i];

            if (!fileToDelete.taxonomy) continue;

            if (shouldHideCategory(fileToDelete.taxonomy, request.current, settings.current.odvetvieId, props.getRequestFieldsRelationTask.value?.fields ?? [])) {
                const deleteInput = DeleteUploadedFileInputSchema.parse({
                    fileId: fileToDelete.id,
                    requestId: request.current.requestId,
                });

                requestApi.deleteUploadedFile(deleteInput);
                requestWithFiles.current.files?.splice(i, 1);
            }
        }

        onlySaveRequest(
            true,
            false,
            undefined,
            sectionChild.children.some((child) => child.field?.fieldsRelationMaster === true),
        );
    });

    const onlyRadioButtonChange = (newValue: string) => {
        sectionChild.groupValue = newValue;
        props.rerenderForm();
        saveRadioButtonTask.debouncedPerform();
    }

    const capitalizeFirstLetter = (value?: string | null) => {
        if (!value) return "";
        return value.charAt(0).toUpperCase() + value.slice(1);
    }

    return <RadioGroup
        value={sectionChild.groupValue || null}
        sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}
        onChange={(e) => onlyRadioButtonChange(e.target.value)}
    >
        {sectionChild.children.map((child: SectionOrFieldDto, index: number) => {
            if (!child.field) return <></>

            if (
                shouldHideFieldOrSection(
                    child.field,
                    request.current,
                    settings.current.odvetvieId,
                    props.getRequestFieldsRelationTask.value?.fields ?? []
                )
            ) {
                return null;
            }

            const isActive = sectionChild.groupValue === child.field.key;
            const isDisabled =
                child.field.isDisabled ||
                settings.current.previewMode;

            return (
                <Tooltip key={child.field.id || index} title={child.field.description || ""}>
                    <Box sx={{
                        width: "100%",
                        border: isActive
                            ? `2px solid ${theme.palette.common.black}`
                            : `1px solid ${alpha(theme.palette.common.black, 0.2)}`,
                        backgroundColor: isActive ? theme.palette.grey[50] : "transparent",
                        borderRadius: "7px",
                        padding: "16px",
                        boxSizing: "border-box",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        transition: "border-color 0.2s ease, background-color 0.2s ease",
                        "&:hover": {
                            borderColor: theme.palette.common.black,
                            backgroundColor: theme.palette.grey[50],
                        },
                    }}
                        onClick={() => {
                            if (isDisabled || !child.field?.key) return;
                            onlyRadioButtonChange(child.field.key);
                        }}
                    >
                        <FormControlLabel
                            sx={{
                                margin: 0,
                                width: "100%",
                                alignItems: "flex-start",
                                "& .MuiFormControlLabel-label": {
                                    width: "100%",
                                },
                            }}
                            value={child.field.key}
                            disabled={isDisabled}
                            control={<Radio
                                sx={{ padding: 0, marginRight: "10px" }}
                                inputProps={{
                                    onKeyDown: (e) => {
                                        if (e.key === "Enter" && !isDisabled && child.field?.key) {
                                            e.preventDefault();
                                            onlyRadioButtonChange(child.field.key);
                                        }
                                    },
                                }}
                            />}
                            label={
                                <Typography
                                    component="span"
                                    sx={{
                                        fontFamily: theme.typography.fontFamily,
                                        fontWeight: 500,
                                        fontSize: "15px",
                                        lineHeight: "148%",
                                        letterSpacing: 0,
                                        color: theme.palette.common.black,
                                    }}
                                >
                                    {capitalizeFirstLetter(child.field.parameterText)}
                                </Typography>
                            }
                        ></FormControlLabel>
                    </Box>
                </Tooltip>
            );
        })}
    </RadioGroup>
}